import { useClampedText, useIsIos, type UseClampedTextType } from '@virginexperiencedays/hooks';
import { cn } from '@virginexperiencedays/tailwind';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
} from 'react';

export type ClampLines = 1 | 2 | 3 | 4 | 5 | 6;

export type CollapsibleProps = {
  lineClampCount: ClampLines;
  overrideMinHeight?: number;
  shouldTruncate?: boolean;
};

type CollapsibleContentContextType = UseClampedTextType<HTMLDivElement> & CollapsibleProps;

const CollapsibleContentContext = createContext<CollapsibleContentContextType | null>(null);

/**
 * Context to provide the clamped text state to the children components
 */
const useCollapsibleContext = () => useContext(CollapsibleContentContext);

/**
 * The Collapsible component is a wrapper that provides the clamped text state to its children.
 * This component should be used as the parent of `CollapsibleContent` and `CollapsibleTrigger` components.
 *
 * @example
 * ```tsx
 * <Collapsible lineClampCount={2}>
 *   <CollapsibleContent>
 *     <p className="text-neutral font-light leading-6">
 *       Sit magna et quis enim minim laborum culpa voluptate irure proident. Nulla laboris occaecat
 *       pariatur in. Est laborum in tempor culpa id amet voluptate aute excepteur ea exercitation
 *       aliquip. Nostrud est veniam aliquip velit dolore consequat quis magna excepteur.
 *     </p>
 *   </CollapsibleContent>
 *   <CollapsibleTrigger />
 * </Collapsible>
 * ```
 */
export const Collapsible = ({
  lineClampCount,
  children,
  className,
  overrideMinHeight = null,
  ...props
}: CollapsibleProps & HTMLAttributes<HTMLDivElement>) => {
  const { isTargetIosVersion } = useIsIos([13, 14, 15, 16]);
  const { isExpanded, clientHeight, scrollHeight, ...rest } = useClampedText<HTMLDivElement>({
    overrideMinHeight,
  });

  const value = useMemo(
    () => ({
      isExpanded,
      clientHeight: clientHeight || null, // do not allow 0 to avoid unintended 0px heights
      scrollHeight: scrollHeight || null,
      lineClampCount,
      shouldTruncate: !isTargetIosVersion,
      ...rest,
    }),
    [
      isExpanded,
      clientHeight,
      scrollHeight,
      lineClampCount,
      overrideMinHeight,
      isTargetIosVersion,
      rest,
    ]
  );

  return (
    <CollapsibleContentContext.Provider value={value}>
      <div className={className} {...props}>
        {children}
      </div>
    </CollapsibleContentContext.Provider>
  );
};

function CollapsibleTruncated({ children }: Readonly<HTMLAttributes<HTMLDivElement>>) {
  const { ref, isExpanded, lineClampCount, shouldTruncate } = useCollapsibleContext();
  const hideTruncated = useHideTruncatedText(isExpanded);

  return (
    <div
      ref={ref}
      data-id="truncated-content"
      className={cn(
        'bg-background-page relative z-10 overflow-hidden',
        shouldTruncate && getLineClampClassName(lineClampCount),
        isExpanded && 'line-clamp-none select-none',
        hideTruncated && 'invisible'
      )}
    >
      {children}
    </div>
  );
}

/**
 * Renders a collapsible content component. This must only be used as a child of the `Collapsible` component
 * and handles the duplicate/dynamic rendering of both the truncated and expanded content.
 */
export function CollapsibleContent({
  children,
  ...props
}: Readonly<HTMLAttributes<HTMLDivElement>>) {
  const { scrollHeight, clientHeight, isExpanded } = useCollapsibleContext();

  const variableStyles = {
    '--text-cover-top': clientHeight ? `${clientHeight}px` : 'auto',
    '--text-cover-height':
      scrollHeight && clientHeight ? `${scrollHeight - clientHeight}px` : 'auto',
  } as CSSProperties;

  const displayContent = useDisplayContent(isExpanded);

  return (
    <div
      data-id="collapsible-content"
      className="transition-height relative isolate w-full overflow-hidden duration-300"
      style={{ height: isExpanded ? scrollHeight : clientHeight, ...variableStyles }}
      aria-expanded={isExpanded}
      {...props}
    >
      <CollapsibleTruncated>{children}</CollapsibleTruncated>
      <div
        data-id="content"
        aria-hidden={!isExpanded}
        className={cn(
          'absolute inset-0 z-0 opacity-0 transition-opacity duration-300',
          isExpanded && 'opacity-100'
        )}
      >
        {displayContent && children}
      </div>
    </div>
  );
}

/**
 * Renders a collapsible toggle/trigger button. This must only be used as a child of the `Collapsible` component
 * and handles the click event to toggle the expanded state of the content.
 *
 * @example
 * ```tsx
 * <CollapsibleTrigger
 *   text=["Read More", "Read Less"]
 *   className="toggle-button"
 *   onClick={handleClick}
 * />
 * ```
 */
export type CollapsibleTriggerProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  text?: [string, string];
  scrollToTop?: boolean;
  onClick?: (data: { isExpanded: boolean; text: string }) => void;
};

export function CollapsibleTrigger({
  text,
  scrollToTop = true,
  onClick,
  disabled,
  className,
  ...props
}: CollapsibleTriggerProps) {
  const { handleToggleExpanded, isExpanded, isExpandable } = useCollapsibleContext();

  const [collapsedText, expandedText] = text ?? ['Read More', 'Read Less'];
  const isDisabled = disabled ?? !isExpandable;
  const displayText = isExpanded ? expandedText : collapsedText;

  return (
    <button
      onClick={() => {
        handleToggleExpanded({ scrollToTop });
        onClick?.({
          isExpanded,
          text: displayText,
        });
      }}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      className={cn(
        'text-brand hidden font-semibold leading-6 underline',
        isExpandable && 'inline-block',
        className
      )}
      {...props}
    >
      {displayText}
    </button>
  );
}

/**
 * Custom hook that delays the boolean value of `isExpanded` for a specified duration and direction.
 */
function useDelayBoolean(isExpanded: boolean, delay: number, reverse = false): boolean {
  const timeoutRef = useRef<number | null>(null);
  const [displayContent, setDisplayContent] = useState(isExpanded);

  useEffect(() => {
    if (typeof window === 'undefined' || timeoutRef.current) return;

    const updateContent = () => setDisplayContent(isExpanded);

    if ((reverse && !isExpanded) || (!reverse && isExpanded)) {
      timeoutRef.current = window.setTimeout(updateContent, delay);
    } else {
      updateContent();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isExpanded, delay, reverse]);

  return displayContent;
}

/**
 * Custom hook that returns a boolean value indicating whether to hide truncated text.
 * Essentially an extension of the `useClampedText` hook.
 */
function useHideTruncatedText(isExpanded: boolean): boolean {
  return useDelayBoolean(isExpanded, 300);
}

/**
 * Custom hook that returns a boolean value indicating whether to display the content.
 * Essentially an extension of the `useClampedText` hook.
 */
function useDisplayContent(isExpanded: boolean): boolean {
  return useDelayBoolean(isExpanded, 300, true);
}

/**
 * Returns the Tailwind CSS class name for line clamping based on the number of lines to clamp.
 */
function getLineClampClassName(clampLines: ClampLines): string {
  const clampConfig = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
    5: 'line-clamp-5',
    6: 'line-clamp-6',
  };
  return clampConfig?.[clampLines] || 'line-clamp-none';
}

export * from './CollapsibleList';
